import React, { useState, useEffect, useCallback } from 'react';
import { affiliateApi, dealsApi } from '../lib/api';
import { formatCurrency, formatDate, formatDateTime } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  Link as LinkIcon, CurrencyDollar, Users, ChartLineUp, Copy,
  Plus, Check, Clock, X, HandCoins, Megaphone, TrendUp,
  WhatsappLogo, FacebookLogo, InstagramLogo, LinkedinLogo, TwitterLogo, EnvelopeSimple, ShareNetwork,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-500 text-white', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-500 text-white', icon: Check },
  paid: { label: 'Paid', color: 'bg-emerald-500 text-white', icon: CurrencyDollar },
  rejected: { label: 'Rejected', color: 'bg-red-500 text-white', icon: X },
};

const TIER_CONFIG = {
  standard: { label: 'Standard', rate: '10%', color: 'bg-zinc-500 text-white',   min: 0 },
  silver:   { label: 'Silver',   rate: '13%', color: 'bg-slate-400 text-white',  min: 5 },
  gold:     { label: 'Gold',     rate: '16%', color: 'bg-amber-500 text-white',  min: 15 },
  platinum: { label: 'Platinum', rate: '20%', color: 'bg-purple-500 text-white', min: 30 },
};

export default function AffiliatePage() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [deals, setDeals] = useState([]);
  const [allAffiliates, setAllAffiliates] = useState([]);
  const [allReferrals, setAllReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isReferralOpen, setIsReferralOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState('');
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, dealsRes] = await Promise.all([
        affiliateApi.getDashboard(),
        dealsApi.getAll({ limit: 100 }),
      ]);
      setDashboard(dashRes.data);
      setDeals(dealsRes.data);
      if (isAdmin) {
        const [affRes, refRes] = await Promise.all([
          affiliateApi.getAllAffiliates().catch(() => ({ data: [] })),
          affiliateApi.getAllReferrals({ limit: 50 }).catch(() => ({ data: [] })),
        ]);
        setAllAffiliates(affRes.data || []);
        setAllReferrals(refRes.data || []);
      }
    } catch (error) {
      toast.error('Failed to load affiliate data');
    } finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // fall through to exec fallback
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const copyReferralLink = async () => {
    const code = dashboard?.profile?.referral_code;
    if (!code) return;
    const ok = await copyToClipboard(`${window.location.origin}?ref=${code}`);
    if (ok) toast.success('Referral link copied!');
    else toast.error('Copy failed — please copy manually from the field');
  };

  const getReferralLink = () => {
    const code = dashboard?.profile?.referral_code;
    return code ? `${window.location.origin}?ref=${code}` : '';
  };

  const getShareMessage = () => {
    const appName = dashboard?.profile?.app_name || 'BIL';
    return `Check out ${appName} — the AI-powered Business Intelligence Lounge I'm using to manage deals, documents and data rooms. Sign up with my link and let's work smarter together!`;
  };

  const shareOn = (platform) => {
    const url = getReferralLink();
    if (!url) return toast.error('Referral link not ready yet');
    const text = getShareMessage();
    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(text);
    let shareUrl = '';
    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
        break;
      case 'instagram':
        // Instagram doesn't support direct URL sharing — copy link + open Instagram
        copyToClipboard(`${text} ${url}`).then(() => {
          toast.success('Link copied! Open Instagram and paste it in your Story, DM or bio.');
          window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
        });
        return;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent('Join me on BIL')}&body=${encodedText}%0A%0A${encodedUrl}`;
        break;
      case 'native':
        if (navigator.share) {
          navigator.share({ title: 'BIL referral', text, url }).catch(() => {});
          return;
        }
        return copyReferralLink();
      default:
        return;
    }
    window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const handleCreateReferral = async () => {
    if (!selectedDeal) { toast.error('Select a deal'); return; }
    try {
      await affiliateApi.createReferral({ deal_id: selectedDeal });
      toast.success('Referral created');
      setIsReferralOpen(false);
      setSelectedDeal('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create referral');
    }
  };

  const handleStatusChange = async (referralId, newStatus) => {
    try {
      await affiliateApi.updateReferralStatus(referralId, newStatus);
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  const profile = dashboard?.profile;
  const tierConfig = TIER_CONFIG[profile?.tier] || TIER_CONFIG.standard;

  return (
    <div className="space-y-6" data-testid="affiliate-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Affiliate Program</h1>
          <p className="text-muted-foreground text-sm mt-1">Refer deals and earn commissions</p>
        </div>
        <Button onClick={() => setIsReferralOpen(true)} className="rounded-sm" data-testid="new-referral-btn">
          <Plus size={18} className="mr-2" /> New Referral
        </Button>
      </div>

      {/* Profile Card */}
      {profile && (
        <div className="bg-card border border-border p-6 rounded-sm space-y-5">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-sm bg-primary flex items-center justify-center">
                <Megaphone size={28} className="text-primary-foreground" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-heading text-xl font-bold">Your Referral Link</h2>
                  <Badge className={tierConfig.color}>{tierConfig.label}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">Commission Rate: {profile.commission_rate}% · Earn on every signup</p>
              </div>
            </div>
          </div>

          {/* Tier progress */}
          <TierProgress profile={profile} nextTier={dashboard?.next_tier} />

          {/* Referral link + copy */}
          <div className="flex flex-col sm:flex-row items-stretch gap-2">
            <div className="flex-1 flex items-center bg-muted rounded-sm border border-border overflow-hidden">
              <LinkIcon size={16} className="mx-3 text-muted-foreground flex-shrink-0" />
              <input
                readOnly
                value={getReferralLink()}
                className="flex-1 bg-transparent text-sm font-mono py-2 focus:outline-none"
                onClick={(e) => e.target.select()}
                data-testid="referral-link-input"
              />
            </div>
            <Button onClick={copyReferralLink} data-testid="copy-referral" className="rounded-sm">
              <Copy size={16} className="mr-2" /> Copy
            </Button>
          </div>

          {/* Social share buttons */}
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-3">Share on</p>
            <div className="flex flex-wrap gap-2">
              <SocialButton
                label="WhatsApp"
                Icon={WhatsappLogo}
                bg="#25D366"
                onClick={() => shareOn('whatsapp')}
                testId="share-whatsapp"
              />
              <SocialButton
                label="Facebook"
                Icon={FacebookLogo}
                bg="#1877F2"
                onClick={() => shareOn('facebook')}
                testId="share-facebook"
              />
              <SocialButton
                label="Instagram"
                Icon={InstagramLogo}
                bg="linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)"
                onClick={() => shareOn('instagram')}
                testId="share-instagram"
              />
              <SocialButton
                label="LinkedIn"
                Icon={LinkedinLogo}
                bg="#0A66C2"
                onClick={() => shareOn('linkedin')}
                testId="share-linkedin"
              />
              <SocialButton
                label="X / Twitter"
                Icon={TwitterLogo}
                bg="#000000"
                onClick={() => shareOn('twitter')}
                testId="share-twitter"
              />
              <SocialButton
                label="Email"
                Icon={EnvelopeSimple}
                bg="#6366f1"
                onClick={() => shareOn('email')}
                testId="share-email"
              />
              {typeof navigator !== 'undefined' && navigator.share && (
                <SocialButton
                  label="More..."
                  Icon={ShareNetwork}
                  bg="#52525b"
                  onClick={() => shareOn('native')}
                  testId="share-native"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPI icon={Users} label="Total Referrals" value={profile?.total_referrals || 0} />
        <KPI icon={CurrencyDollar} label="Total Earned" value={formatCurrency(profile?.total_earned || 0)} sub="Lifetime" />
        <KPI icon={Clock} label="Pending Commission" value={formatCurrency(profile?.pending_commission || 0)} sub="Awaiting approval" />
        <KPI icon={HandCoins} label="Total Paid" value={formatCurrency(profile?.total_paid || 0)} sub="Withdrawn" />
      </div>

      <Tabs defaultValue="referrals">
        <TabsList>
          <TabsTrigger value="referrals"><LinkIcon size={16} className="mr-1" /> My Referrals</TabsTrigger>
          <TabsTrigger value="analytics"><ChartLineUp size={16} className="mr-1" /> Analytics</TabsTrigger>
          {isAdmin && <TabsTrigger value="manage"><Users size={16} className="mr-1" /> Manage All</TabsTrigger>}
        </TabsList>

        <TabsContent value="referrals" className="space-y-4">
          {dashboard?.recent_referrals?.length === 0 ? (
            <div className="text-center p-12 border border-dashed border-border rounded-sm">
              <LinkIcon size={40} className="mx-auto text-muted-foreground mb-3 opacity-40" />
              <p className="text-muted-foreground">No referrals yet</p>
              <p className="text-xs text-muted-foreground mt-1">Create a referral to start earning commissions</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Deal</th>
                    <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Deal Value</th>
                    <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Commission</th>
                    <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Status</th>
                    <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.recent_referrals?.map((ref) => {
                    const sc = STATUS_CONFIG[ref.status] || STATUS_CONFIG.pending;
                    return (
                      <tr key={ref.id} className="border-b border-border hover:bg-muted/30" data-testid={`referral-${ref.id}`}>
                        <td className="p-3 font-medium">{ref.deal_title || 'N/A'}</td>
                        <td className="p-3 text-right">{formatCurrency(ref.deal_value || 0)}</td>
                        <td className="p-3 text-right font-semibold text-primary">{formatCurrency(ref.commission_amount || 0)}</td>
                        <td className="p-3"><Badge className={`${sc.color} text-xs`}>{sc.label}</Badge></td>
                        <td className="p-3 text-sm text-muted-foreground">{formatDate(ref.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <div className="bg-card border border-border p-6 rounded-sm">
            <h3 className="font-heading text-lg font-semibold mb-4">Monthly Commissions</h3>
            <div className="h-72">
              {dashboard?.monthly_stats?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dashboard.monthly_stats}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                    <Bar dataKey="commission" fill="hsl(var(--primary))" name="Commission" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-muted-foreground text-center h-full flex items-center justify-center">No data yet</p>}
            </div>
          </div>
          {/* Status Breakdown */}
          {dashboard?.status_stats && Object.keys(dashboard.status_stats).length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(dashboard.status_stats).map(([status, data]) => {
                const sc = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
                return (
                  <div key={status} className="bg-card border border-border p-4 rounded-sm">
                    <Badge className={`${sc.color} text-xs mb-2`}>{sc.label}</Badge>
                    <p className="text-xl font-bold">{data.count}</p>
                    <p className="text-sm text-muted-foreground">{formatCurrency(data.total)}</p>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="manage" className="space-y-6">
            {/* All Affiliates */}
            <div className="bg-card border border-border rounded-sm overflow-hidden">
              <div className="p-4 border-b border-border">
                <h3 className="font-heading font-semibold">All Affiliates</h3>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Affiliate</th>
                    <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Tier</th>
                    <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Rate</th>
                    <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Referrals</th>
                    <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Earned</th>
                    <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {allAffiliates.map((aff) => (
                    <tr key={aff.id} className="border-b border-border hover:bg-muted/30">
                      <td className="p-3"><p className="font-medium">{aff.user_name}</p><p className="text-xs text-muted-foreground">{aff.user_email}</p></td>
                      <td className="p-3"><Badge className={`${TIER_CONFIG[aff.tier]?.color || 'bg-zinc-500 text-white'} text-xs`}>{aff.tier}</Badge></td>
                      <td className="p-3 text-right">{aff.commission_rate}%</td>
                      <td className="p-3 text-right">{aff.total_referrals}</td>
                      <td className="p-3 text-right font-semibold">{formatCurrency(aff.total_earned)}</td>
                      <td className="p-3 text-right">{formatCurrency(aff.pending_commission)}</td>
                    </tr>
                  ))}
                  {allAffiliates.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No affiliates</td></tr>}
                </tbody>
              </table>
            </div>

            {/* All Referrals with status management */}
            <div className="bg-card border border-border rounded-sm overflow-hidden">
              <div className="p-4 border-b border-border">
                <h3 className="font-heading font-semibold">All Referrals</h3>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Referrer</th>
                    <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Deal</th>
                    <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Commission</th>
                    <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Status</th>
                    <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allReferrals.map((ref) => (
                    <tr key={ref.id} className="border-b border-border hover:bg-muted/30">
                      <td className="p-3 font-medium">{ref.referrer_name}</td>
                      <td className="p-3">{ref.deal_title || 'N/A'}<br /><span className="text-xs text-muted-foreground">{formatCurrency(ref.deal_value || 0)}</span></td>
                      <td className="p-3 text-right font-semibold text-primary">{formatCurrency(ref.commission_amount || 0)}</td>
                      <td className="p-3"><Badge className={`${STATUS_CONFIG[ref.status]?.color || 'bg-zinc-500 text-white'} text-xs`}>{ref.status}</Badge></td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          {ref.status === 'pending' && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => handleStatusChange(ref.id, 'approved')} data-testid={`approve-${ref.id}`}>Approve</Button>
                              <Button size="sm" variant="destructive" onClick={() => handleStatusChange(ref.id, 'rejected')}>Reject</Button>
                            </>
                          )}
                          {ref.status === 'approved' && (
                            <Button size="sm" onClick={() => handleStatusChange(ref.id, 'paid')} data-testid={`pay-${ref.id}`}>Mark Paid</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {allReferrals.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No referrals</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Create Referral Dialog */}
      <Dialog open={isReferralOpen} onOpenChange={setIsReferralOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Referral</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Link a deal to earn {profile?.commission_rate || 5}% commission on its value.
            </p>
            <div className="space-y-2">
              <Label>Select Deal *</Label>
              <Select value={selectedDeal || "pick_deal"} onValueChange={(v) => setSelectedDeal(v === "pick_deal" ? "" : v)}>
                <SelectTrigger data-testid="select-deal">
                  <SelectValue placeholder="Select a deal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pick_deal" disabled>Select a deal</SelectItem>
                  {deals.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.title} - {formatCurrency(d.value)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedDeal && deals.find(d => d.id === selectedDeal) && (
              <div className="p-3 bg-muted rounded-sm">
                <p className="text-sm font-medium">{deals.find(d => d.id === selectedDeal)?.title}</p>
                <p className="text-xs text-muted-foreground">
                  Value: {formatCurrency(deals.find(d => d.id === selectedDeal)?.value || 0)} |
                  Commission: {formatCurrency((deals.find(d => d.id === selectedDeal)?.value || 0) * (profile?.commission_rate || 5) / 100)}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReferralOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateReferral} disabled={!selectedDeal} data-testid="create-referral-btn">Create Referral</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-card border border-border p-4 rounded-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
          <Icon size={20} className="text-primary" />
        </div>
      </div>
    </div>
  );
}

function SocialButton({ label, Icon, bg, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="group flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium hover:scale-105 active:scale-95 transition-transform shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
      style={{ background: bg }}
      title={`Share on ${label}`}
    >
      <Icon size={18} weight="fill" />
      <span>{label}</span>
    </button>
  );
}

function TierProgress({ profile, nextTier }) {
  const current = profile?.total_referrals || 0;
  const tiers = [
    { id: 'standard', label: 'Standard', rate: 10, min: 0 },
    { id: 'silver',   label: 'Silver',   rate: 13, min: 5 },
    { id: 'gold',     label: 'Gold',     rate: 16, min: 15 },
    { id: 'platinum', label: 'Platinum', rate: 20, min: 30 },
  ];
  const currentIdx = tiers.findIndex(t => t.id === profile?.tier);
  const next = nextTier || (currentIdx < tiers.length - 1 ? tiers[currentIdx + 1] : null);
  const prevMin = currentIdx >= 0 ? tiers[currentIdx].min : 0;
  const nextMin = next ? (next.min_referrals ?? next.min) : prevMin;
  const pct = next ? Math.min(100, ((current - prevMin) / Math.max(1, nextMin - prevMin)) * 100) : 100;
  const remaining = next ? Math.max(0, nextMin - current) : 0;
  const nextRate = next ? (next.commission_rate ?? next.rate) : null;

  return (
    <div className="bg-muted/40 border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Commission Tiers</p>
          <p className="text-sm mt-1">
            {next
              ? <>You're <strong>{remaining} referral{remaining === 1 ? '' : 's'}</strong> away from <strong>{next.label || next.id}</strong> ({nextRate}% commission)</>
              : <><strong>Top tier!</strong> You're earning the maximum commission rate.</>}
          </p>
        </div>
      </div>
      {/* Progress bar */}
      <div className="relative h-2 bg-background rounded-full overflow-hidden mb-3">
        <div
          className="h-full bg-gradient-to-r from-emerald-500 via-amber-500 to-purple-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      {/* Tier steps */}
      <div className="grid grid-cols-4 gap-2">
        {tiers.map((t) => {
          const reached = current >= t.min;
          const isCurrent = profile?.tier === t.id;
          return (
            <div
              key={t.id}
              className={`text-center p-2 rounded-md border text-xs transition-all ${
                isCurrent
                  ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
                  : reached
                  ? 'border-emerald-500/40 bg-emerald-500/5'
                  : 'border-border bg-card/50 opacity-60'
              }`}
            >
              <p className="font-semibold">{t.label}</p>
              <p className="text-xs text-muted-foreground">{t.min}+ refs</p>
              <p className={`font-bold mt-1 ${isCurrent ? 'text-primary' : reached ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                {t.rate}%
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}


